"use client";

import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ArrowRight, ArrowLeft, CircleCheck, Loader, Sparkles, ShieldCheck, Briefcase } from "lucide-react";
const inputClasses = "flex h-12 w-full rounded-none border-0 border-b border-border bg-transparent px-0 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-300";
export default function StartYourJourney() {
  return <section data-section-id="79246" id="start-a-project" className="dark bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            {}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-30" />
            
            <div className="max-w-3xl mx-auto relative z-10">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center p-3 bg-primary/5 rounded-none mb-6 border border-primary/20">
                        <ShieldCheck className="size-6 text-primary" />
                    </div>
                    <h2 className="text-4xl md:text-6xl font-medium tracking-tight mb-6 font-default">Initiate Your Venture</h2>
                    <p className="text-muted-foreground text-xl max-w-2xl mx-auto leading-relaxed">
                        A selective partnership for founders ready to validate, build, and scale. Select your path to begin the conversation.
                    </p>
                </div>

                <FormProvider formKey="start_project_form_contact" formId="start_project_form_contact" sectionName="Start a Project" defaultValues={{
        name: "",
        email: "",
        phone: "",
        company: "",
        project_description: "",
        budget: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        project_description: {
          required: true
        }
      }} submitText="Submit Inquiry" successMessage="Your inquiry has been received. Our team of operators will review your details and reach out within 48 hours.">
                    <JourneyMultiStepForm />
                </FormProvider>
            </div>
        </section>;
}
function JourneyMultiStepForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const progress = step / totalSteps * 100;
  const needsOptions = ["Strategic Advisory", "Growth Operations", "Venture Studio Inquiry", "Technical Execution", "Capital Strategy", "Crowdfunding Readiness"];
  const [selectedNeeds, setSelectedNeeds] = useState<string[]>([]);
  const handleNeedToggle = (need: string) => {
    setSelectedNeeds(prev => prev.includes(need) ? prev.filter(n => n !== need) : [...prev, need]);
  };
  if (isSubmitted && !submitError) {
    return <div className="p-16 text-center bg-card border border-border/50 rounded-none shadow-2xl flex flex-col items-center animate-in fade-in zoom-in duration-700">
                <div className="size-24 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-8 ring-1 ring-primary/30">
                    <CircleCheck className="size-12" />
                </div>
                <h3 className="text-3xl font-medium mb-4 text-foreground font-default">Inquiry Received</h3>
                <p className="text-muted-foreground text-lg max-w-md">{successMessage}</p>
            </div>;
  }
  return <div className="bg-card border border-border/30 rounded-none shadow-2xl p-8 md:p-16 relative overflow-hidden">
            {}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 blur-[120px] pointer-events-none" />

            <div className="mb-12 relative z-10">
                <div className="flex justify-between text-sm font-medium text-muted-foreground mb-4">
                    <span className="uppercase tracking-widest text-xs font-medium text-primary">Phase 0{step}</span>
                    <span className="font-mono text-xs opacity-60">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-0.5 bg-muted rounded-none" />
            </div>

            <Form onSubmit={handleSubmit} className="space-y-10 relative z-10">
                {}
                <div className={step === 1 ? "block animate-in fade-in slide-in-from-right-4 duration-700" : "hidden"}>
                    <div className="mb-10">
                        <h3 className="text-2xl font-medium flex items-center gap-4 mb-3 text-foreground font-default">
                            <Briefcase className="size-5 text-primary" /> 
                            Engagement Scale
                        </h3>
                        <p className="text-muted-foreground text-lg">Identify your current stage to help us align our resources.</p>
                    </div>

                    <FormField control={control} name="budget" render={({
          field
        }) => <FormItem>
                                <FormControl>
                                    <ToggleGroup type="single" value={field.value} onValueChange={field.onChange} className="flex flex-col sm:flex-row flex-wrap gap-4 justify-start">
                                        {["Pre-Seed / Bootstrapped", "Seed Stage", "Series A+", "Established SMB", "Venture Studio"].map((option, i) => <ToggleGroupItem key={i} value={option} data-index={i} variant="outline" className="w-full sm:w-auto px-8 py-7 text-sm font-medium border-border/50 rounded-none hover:bg-primary/10 hover:text-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary transition-all duration-300">
                                                {option}
                                            </ToggleGroupItem>)}
                                    </ToggleGroup>
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />
                </div>

                {}
                <div className={step === 2 ? "block animate-in fade-in slide-in-from-right-4 duration-700" : "hidden"}>
                    <div className="mb-10">
                        <h3 className="text-2xl font-medium flex items-center gap-4 mb-3 text-foreground font-default">
                            <Sparkles className="size-5 text-primary" />
                            Project Vision
                        </h3>
                        <p className="text-muted-foreground text-lg">Define the core requirements of your upcoming venture.</p>
                    </div>

                    <div className="mb-10">
                        <FormLabel className="mb-4 block text-xs uppercase tracking-widest text-primary font-medium">Strategic Focus Areas</FormLabel>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {needsOptions.map((need, i) => <div key={i} data-index={i} className={`flex items-center space-x-4 p-5 rounded-none border transition-all duration-300 cursor-pointer ${selectedNeeds.includes(need) ? 'border-primary bg-primary/5' : 'border-border/30 bg-background/30 hover:border-primary/40'}`} onClick={() => handleNeedToggle(need)}>
                                    <Checkbox id={`need-${i}`} checked={selectedNeeds.includes(need)} onCheckedChange={() => handleNeedToggle(need)} className="data-[state=checked]:bg-primary data-[state=checked]:border-primary border-border/50 rounded-none" />
                                    <label htmlFor={`need-${i}`} className="text-base font-medium leading-none cursor-pointer flex-1 text-foreground">
                                        {need}
                                    </label>
                                </div>)}
                        </div>
                    </div>

                    <FormField control={control} name="project_description" rules={{
          required: "Please provide a brief overview."
        }} render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-xs uppercase tracking-widest text-primary font-medium">Detailed Overview <span className="text-destructive">*</span></FormLabel>
                                <FormControl>
                                    <Textarea placeholder="Describe your objectives, current traction, and where you need operator-level support." className="min-h-[180px] resize-none bg-background/30 border-0 border-b border-border/50 rounded-none focus-visible:ring-0 focus-visible:border-primary px-0 py-3 text-base" {...field} />
                                </FormControl>
                                <FormDescription className="text-muted-foreground/60 italic text-sm mt-2">Precision here allows for a more focused initial consultation.</FormDescription>
                                <FormMessage />
                            </FormItem>} />
                </div>

                {}
                <div className={step === 3 ? "block animate-in fade-in slide-in-from-right-4 duration-700" : "hidden"}>
                    <div className="mb-10">
                        <h3 className="text-2xl font-medium mb-3 text-foreground font-default">Direct Line</h3>
                        <p className="text-muted-foreground text-lg">Provide your details for a confidential follow-up.</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                        <FormField control={control} name="name" rules={{
            required: "Name is required"
          }} render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xs uppercase tracking-widest text-primary font-medium">Full Name <span className="text-destructive">*</span></FormLabel>
                                    <FormControl>
                                        <input type="text" className={inputClasses} placeholder="Alexander Sterling" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                        <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^\S+@\S+$/i,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xs uppercase tracking-widest text-primary font-medium">Professional Email <span className="text-destructive">*</span></FormLabel>
                                    <FormControl>
                                        <input type="email" className={inputClasses} placeholder="sterling@ventures.com" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                        <FormField control={control} name="phone" rules={{
            required: "Phone number is required"
          }} render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xs uppercase tracking-widest text-primary font-medium">Phone <span className="text-destructive">*</span></FormLabel>
                                    <FormControl>
                                        <input type="tel" className={inputClasses} placeholder="+1 (555) 000-0000" {...field} />
                                    </FormControl>
                                    <FormDescription className="text-[10px] uppercase tracking-widest opacity-50">Confidential contact only.</FormDescription>
                                    <FormMessage />
                                </FormItem>} />
                        <FormField control={control} name="company" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-xs uppercase tracking-widest text-primary font-medium">Company / Venture Name</FormLabel>
                                    <FormControl>
                                        <input type="text" className={inputClasses} placeholder="1066 Industries" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />
                    </div>
                </div>

                {submitError && <div className="p-5 bg-destructive/5 border border-destructive/20 rounded-none text-destructive text-sm font-medium font-mono">
                        {submitError}
                    </div>}

                {}
                <div className="flex items-center justify-between pt-12 border-t border-border/20 mt-12">
                    {step > 1 ? <Button type="button" variant="ghost" onClick={() => setStep(step - 1)} className="text-muted-foreground hover:text-foreground hover:bg-transparent px-0">
                            <ArrowLeft className="mr-3 size-4" /> Previous
                        </Button> : <div />}

                    {step < totalSteps ? <Button type="button" onClick={() => setStep(step + 1)} className="px-10 py-6 rounded-none bg-primary text-primary-foreground hover:opacity-90 transition-opacity">
                            Continue <ArrowRight className="ml-3 size-4" />
                        </Button> : <Button type="submit" disabled={isSubmitting} className="px-10 py-6 rounded-none bg-primary text-primary-foreground hover:opacity-90 transition-opacity">
                            {isSubmitting ? <><Loader className="mr-3 size-4 animate-spin" /> Transmitting...</> : <><CircleCheck className="mr-3 size-4" /> Submit Inquiry</>}
                        </Button>}
                </div>
            </Form>
        </div>;
}